import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { MemberPhotoUploader } from '@/components/shared/member-photo-uploader';
import { MemberTitleField } from '@/components/shared/member-title-field';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { PhoneInput } from '@/components/ui/phone-input';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { uploadBlob } from '@/lib/upload';
import { LeadershipSubnav } from '@/pages/people/leadership-subnav';
import { useChurchStore } from '@/stores/church-store';

interface Minister {
  id: string;
  memberId: string;
  memberName: string;
  title: string;
  role: string;
  ordainedAt: string | null;
  email: string | null;
  phone: string | null;
  alternatePhone: string | null;
  photoKey: string | null;
  photoUrl: string | null;
  termStartYear: number;
  termEndYear: number | null;
  isActive: boolean;
  sortOrder: number;
}

interface Person {
  id: string;
  firstName: string;
  lastName: string;
}

const personLabel = (person: Pick<Person, 'firstName' | 'lastName'>): string =>
  `${person.firstName} ${person.lastName}`.trim();

const currentYear = new Date().getFullYear();

const yearOptions = Array.from({ length: currentYear - 1949 + 1 }, (_, index) => {
  const year = currentYear - index;
  return { value: String(year), label: String(year) };
});

const emptyForm = () => ({
  memberId: null as string | null,
  memberQuery: '',
  selectedMember: null as { id: string; label: string } | null,
  title: '',
  role: '',
  email: '',
  phone: null as string | null,
  alternatePhone: null as string | null,
  photoKey: null as string | null,
  photoPreviewUrl: null as string | null,
  ordainedAt: null as string | null,
  termStartYear: currentYear,
  currentlyServing: true,
  termEndYear: null as number | null,
  isActive: true,
});

export const MinistersPage = () => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useChurchStore((state) => state.membership);

  const canManage = hasPermission(membership, PERMISSIONS.PEOPLE_MANAGE_MINISTERS);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Minister | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Minister | null>(null);
  const [form, setForm] = useState(emptyForm);

  const list = useQuery({
    queryKey: ['people', 'ministers'],
    queryFn: () => api.get<{ data: Minister[] }>('/people/ministers?pageSize=100'),
  });

  const rows = list.data?.data ?? [];

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'minister-form', form.memberQuery],
    queryFn: () =>
      api.get<{ data: Person[] }>(
        `/people/members?pageSize=50&status=active${
          form.memberQuery ? `&q=${encodeURIComponent(form.memberQuery)}` : ''
        }`,
      ),
    enabled: open && !editing,
    placeholderData: keepPreviousData,
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((person) => ({
        id: person.id,
        label: personLabel(person),
      })),
    [members.data?.data],
  );

  const resetAndClose = () => {
    if (form.photoPreviewUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(form.photoPreviewUrl);
    }
    setOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row: Minister) => {
    setEditing(row);
    setForm({
      memberId: row.memberId,
      memberQuery: '',
      selectedMember: { id: row.memberId, label: row.memberName },
      title: row.title,
      role: row.role,
      email: row.email ?? '',
      phone: row.phone,
      alternatePhone: row.alternatePhone,
      photoKey: row.photoKey,
      photoPreviewUrl: row.photoUrl,
      ordainedAt: row.ordainedAt,
      termStartYear: row.termStartYear,
      currentlyServing: row.termEndYear == null,
      termEndYear: row.termEndYear,
      isActive: row.isActive,
    });
    setOpen(true);
  };

  const formatTerm = (row: Minister) =>
    row.termEndYear == null
      ? t('ministers.termPresent', { start: row.termStartYear })
      : t('ministers.termRange', { start: row.termStartYear, end: row.termEndYear });

  const create = useMutation({
    mutationFn: () =>
      api.post<Minister>('/people/ministers', {
        memberId: form.memberId,
        title: form.title.trim(),
        role: form.role.trim(),
        email: form.email.trim() || null,
        phone: form.phone,
        alternatePhone: form.alternatePhone,
        photoKey: form.photoKey,
        ordainedAt: form.ordainedAt,
        termStartYear: form.termStartYear,
        termEndYear: form.currentlyServing ? null : form.termEndYear,
        isActive: form.isActive,
      }),
    onSuccess: () => {
      toast.success(t('ministers.saved'));
      resetAndClose();
      void queryClient.invalidateQueries({ queryKey: ['people', 'ministers'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const update = useMutation({
    mutationFn: () =>
      api.patch<Minister>(`/people/ministers/${editing!.id}`, {
        title: form.title.trim(),
        role: form.role.trim(),
        email: form.email.trim() || null,
        phone: form.phone,
        alternatePhone: form.alternatePhone,
        photoKey: form.photoKey,
        ordainedAt: form.ordainedAt,
        termStartYear: form.termStartYear,
        termEndYear: form.currentlyServing ? null : form.termEndYear,
        isActive: form.isActive,
      }),
    onSuccess: () => {
      toast.success(t('ministers.updated'));
      resetAndClose();
      void queryClient.invalidateQueries({ queryKey: ['people', 'ministers'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (ministerId: string) => api.delete(`/people/ministers/${ministerId}`),
    onSuccess: () => {
      toast.success(t('ministers.removed'));
      setRemoveTarget(null);
      resetAndClose();
      void queryClient.invalidateQueries({ queryKey: ['people', 'ministers'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const reorder = useMutation({
    mutationFn: (ministerIds: string[]) =>
      api.post<{ data: Minister[] }>('/people/ministers/reorder', { ministerIds }),
    onSuccess: (result) => {
      queryClient.setQueryData(['people', 'ministers'], result);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const columns: DataTableColumn<Minister>[] = [
    {
      id: 'name',
      header: t('ministers.columns.person'),
      cell: (row) => (
        <MemberNameLink
          memberId={row.memberId}
          name={row.memberName}
          photoUrl={row.photoUrl}
          showPhoto
        />
      ),
    },
    {
      id: 'title',
      header: t('ministers.columns.title'),
      cell: (row) => row.title,
    },
    {
      id: 'role',
      header: t('ministers.columns.role'),
      cell: (row) => row.role,
    },
    {
      id: 'term',
      header: t('ministers.columns.term'),
      cell: (row) => formatTerm(row),
    },
    {
      id: 'status',
      header: t('ministers.columns.status'),
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'neutral'}>
          {row.isActive ? t('members.status.active') : t('members.status.inactive')}
        </Badge>
      ),
    },
  ];

  const termInvalid =
    !form.currentlyServing &&
    (form.termEndYear == null || form.termEndYear < form.termStartYear);
  const canSave =
    Boolean(form.memberId) &&
    form.title.trim().length > 0 &&
    form.role.trim().length > 0 &&
    !termInvalid;
  const saving = create.isPending || update.isPending;

  const photoLabels = {
    change: t('members.form.photoChange'),
    remove: t('members.form.photoRemove'),
    cropTitle: t('members.form.photoCropTitle'),
    zoom: tCommon('media.zoom'),
    apply: t('members.form.photoApply'),
    cancel: tCommon('action.cancel'),
    instructions: t('members.form.photoInstructions'),
    closeCropper: tCommon('action.close'),
  };

  const handlePhotoUpload = async (blob: Blob) => {
    try {
      const { key } = await uploadBlob({ purpose: 'minister_photo', blob });
      const preview = URL.createObjectURL(blob);
      setForm((current) => {
        if (current.photoPreviewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(current.photoPreviewUrl);
        }
        return { ...current, photoKey: key, photoPreviewUrl: preview };
      });
    } catch (err) {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
      throw err;
    }
  };

  const handlePhotoRemove = () => {
    setForm((current) => {
      if (current.photoPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(current.photoPreviewUrl);
      }
      return { ...current, photoKey: null, photoPreviewUrl: null };
    });
  };

  return (
    <div>
      <LeadershipSubnav />
      <PageHeader
        title={t('ministers.title')}
        description={t('ministers.description')}
        tipId="members.ministers.purpose"
        headerActions={
          canManage ? <Button onClick={openCreate}>{t('ministers.create')}</Button> : undefined
        }
      />
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t('ministers.empty')}</p>}
        onRowClick={canManage ? openEdit : undefined}
        reorder={
          canManage
            ? {
                handleLabel: tCommon('action.dragToReorder'),
                disabled: reorder.isPending,
                onReorder: async (ministerIds) => {
                  await reorder.mutateAsync(ministerIds);
                },
              }
            : undefined
        }
      />

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) resetAndClose();
          else setOpen(true);
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>
              {editing ? t('ministers.edit') : t('ministers.create')}
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSave) return;
              if (editing) update.mutate();
              else create.mutate();
            }}
          >
            <DialogBody className="overflow-visible" contentClassName="space-y-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
                <div className="min-w-0 flex-1 space-y-4">
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('ministers.person')}</Label>
                    {editing ? (
                      <p className="text-body-sm font-medium text-fg">
                        {form.selectedMember?.label}
                      </p>
                    ) : (
                      <EntityCombobox
                        value={form.memberId}
                        onChange={(id) => {
                          if (!id) {
                            setForm((current) => ({
                              ...current,
                              memberId: null,
                              selectedMember: null,
                            }));
                            return;
                          }
                          const option = memberOptions.find((item) => item.id === id) ?? null;
                          setForm((current) => ({
                            ...current,
                            memberId: id,
                            selectedMember: option,
                          }));
                        }}
                        options={memberOptions}
                        selected={form.selectedMember}
                        onQueryChange={(memberQuery) =>
                          setForm((current) => ({ ...current, memberQuery }))
                        }
                        loading={members.isFetching}
                        placeholder={t('ministers.personPlaceholder')}
                        emptyMessage={t('ministers.personEmpty')}
                        clearLabel={tCommon('action.clearSelection')}
                        popoverPortal="dialog"
                      />
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="minister-title">{t('ministers.titleField')}</Label>
                    <MemberTitleField
                      value={form.title}
                      onChange={(title) =>
                        setForm((current) => ({ ...current, title }))
                      }
                      clearLabel={tCommon('action.clearSelection')}
                      popoverPortal="dialog"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="minister-role">{t('ministers.role')}</Label>
                    <Input
                      id="minister-role"
                      value={form.role}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, role: event.target.value }))
                      }
                      placeholder={t('ministers.rolePlaceholder')}
                      required
                    />
                  </div>
                </div>

                <div className="w-[7.5rem] shrink-0 self-center sm:self-start">
                  <p className="mb-1.5 text-center text-caption text-fg-secondary">
                    {t('ministers.photo')}
                  </p>
                  <MemberPhotoUploader
                    src={form.photoPreviewUrl}
                    name={form.selectedMember?.label ?? ''}
                    size="xl"
                    className="flex-col items-center"
                    onUpload={handlePhotoUpload}
                    onRemove={handlePhotoRemove}
                    labels={photoLabels}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="minister-email">{t('ministers.email')}</Label>
                  <Input
                    id="minister-email"
                    type="email"
                    value={form.email}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, email: event.target.value }))
                    }
                    placeholder={t('ministers.emailPlaceholder')}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>{t('ministers.phone')}</Label>
                  <PhoneInput
                    value={form.phone}
                    onChange={(phone) => setForm((current) => ({ ...current, phone }))}
                    popoverPortal="dialog"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>{t('ministers.alternatePhone')}</Label>
                  <PhoneInput
                    value={form.alternatePhone}
                    onChange={(alternatePhone) =>
                      setForm((current) => ({ ...current, alternatePhone }))
                    }
                    popoverPortal="dialog"
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('ministers.termStart')}</Label>
                    <Listbox
                      value={String(form.termStartYear)}
                      onChange={(value) => {
                        const year = Number(value);
                        if (!Number.isFinite(year)) return;
                        setForm((current) => ({
                          ...current,
                          termStartYear: year,
                          termEndYear:
                            current.termEndYear != null && current.termEndYear < year
                              ? year
                              : current.termEndYear,
                        }));
                      }}
                      options={yearOptions}
                      popoverPortal="dialog"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('ministers.termEnd')}</Label>
                    <Listbox
                      value={
                        form.currentlyServing || form.termEndYear == null
                          ? null
                          : String(form.termEndYear)
                      }
                      onChange={(value) => {
                        if (!value) return;
                        const year = Number(value);
                        if (!Number.isFinite(year)) return;
                        setForm((current) => ({
                          ...current,
                          currentlyServing: false,
                          termEndYear: year,
                        }));
                      }}
                      options={yearOptions.filter(
                        (option) => Number(option.value) >= form.termStartYear,
                      )}
                      disabled={form.currentlyServing}
                      placeholder={t('ministers.termEndPlaceholder')}
                      popoverPortal="dialog"
                    />
                  </div>
                </div>

                {termInvalid ? (
                  <p className="text-caption text-danger">{t('ministers.termInvalid')}</p>
                ) : null}

                <div className="space-y-4 rounded-lg border border-border bg-surface-sunken/40 p-4 pr-5">
                  <SwitchField
                    id="minister-present"
                    label={t('ministers.currentlyServing')}
                    tipId="members.ministers.currentlyServing"
                    checked={form.currentlyServing}
                    onCheckedChange={(currentlyServing) =>
                      setForm((current) => ({
                        ...current,
                        currentlyServing,
                        termEndYear: currentlyServing
                          ? null
                          : (current.termEndYear ?? current.termStartYear),
                      }))
                    }
                  />
                  <SwitchField
                    id="minister-active"
                    label={t('ministers.activeLabel')}
                    tipId="members.ministers.active"
                    checked={form.isActive}
                    onCheckedChange={(isActive) =>
                      setForm((current) => ({ ...current, isActive }))
                    }
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label>{t('ministers.ordainedAt')}</Label>
                  <DatePicker
                    value={form.ordainedAt}
                    onChange={(ordainedAt) => setForm((current) => ({ ...current, ordainedAt }))}
                    popoverPortal="dialog"
                  />
                </div>
              </div>
            </DialogBody>

            <div className="flex items-center justify-between gap-3">
              {editing ? (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setRemoveTarget(editing)}
                >
                  {t('ministers.remove')}
                </Button>
              ) : (
                <span />
              )}
              <DialogFooter className="flex-1 sm:justify-end">
                <Button type="button" variant="outline" onClick={resetAndClose}>
                  {tCommon('action.cancel')}
                </Button>
                <Button type="submit" loading={saving} disabled={!canSave}>
                  {t('ministers.save')}
                </Button>
              </DialogFooter>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setRemoveTarget(null);
        }}
        title={t('ministers.confirmRemove.title', { name: removeTarget?.memberName ?? '' })}
        description={t('ministers.confirmRemove.description')}
        confirmLabel={t('ministers.confirmRemove.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (removeTarget) remove.mutate(removeTarget.id);
        }}
      />
    </div>
  );
};
